import {
  checkedChoices,
  clearStoredAnswers,
  collectAnswerTags,
  exceedsSelectionLimit,
  formatAnswerNote,
  formatGreeting,
  formatProgress,
  progressState,
  QuizStepType,
  rankCandidates,
  readChoiceAnswer,
  readOptionAnswer,
  readStoredAnswers,
  readStoredName,
  restoreCheckedChoices,
  showRankedCandidates,
  writeStoredAnswers,
  writeStoredName
} from '@theme/quiz-answers'

const QuizConfig = {
  storagePrefix: 'quiz-answers:',
  namePrefix: 'quiz-name:',
  defaultResultCount: 3,
  guidedPresentation: 'guided'
}

const QuizAction = {
  back: 'back',
  next: 'next',
  restart: 'restart',
  start: 'start'
}

const QuizSelector = {
  action: '[data-quiz-action]',
  answersInput: '[data-quiz-answers-input]',
  candidate: '[data-quiz-candidate]',
  choice: '[data-quiz-choice]',
  focus: '[data-quiz-focus]',
  formError: '[data-quiz-form-error]',
  formSuccess: '[data-quiz-form-success="true"]',
  legacyFocus:
    '[data-quiz-option], input:not([type="hidden"]), button, textarea, select, [href]',
  nameInput: '[data-quiz-name-input]',
  nameValue: '[data-quiz-name-value]',
  option: '[data-quiz-option]',
  personalize: '[data-quiz-personalize]',
  progressBar: '[data-quiz-progress-bar]',
  progressText: '[data-quiz-progress-text]',
  resultsEmpty: '[data-quiz-results-empty]',
  resultsStep: '[data-quiz-step-type="results"]',
  status: '[data-quiz-status]',
  step: '[data-quiz-step]',
  visibleOn: '[data-quiz-visible-on]'
}

class QuizFlow extends HTMLElement {
  /** @param {MouseEvent} event */
  handleClick = (event) => {
    if (!(event.target instanceof Element)) {
      return
    }

    const option = event.target.closest(QuizSelector.option)
    if (option instanceof HTMLElement && this.contains(option)) {
      this.answerOption(option)
      return
    }

    const control = event.target.closest(QuizSelector.action)
    if (control instanceof HTMLElement && this.contains(control)) {
      this.runAction(control.dataset.quizAction)
    }
  }

  /** @param {Event} event */
  handleChange = (event) => {
    const choice = event.target
    if (!(choice instanceof HTMLInputElement)) {
      return
    }

    const step = choice.closest(QuizSelector.step)
    if (!(step instanceof HTMLElement && choice.matches(QuizSelector.choice))) {
      return
    }

    this.enforceSelectionLimit(step, choice)

    if (!this.isGuided && choice.checked) {
      this.advanceQuestion()
    }
  }

  connectedCallback() {
    this.steps = Array.from(this.querySelectorAll(QuizSelector.step))
    if (!this.steps.length) {
      return
    }

    this.collectElements()
    this.addEventListener('click', this.handleClick)
    this.addEventListener('change', this.handleChange)
    this.restoreName()

    if (this.resumeAfterSubmit()) {
      return
    }
    this.goToStep(this.initialStepIndex(), false)
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.handleClick)
    this.removeEventListener('change', this.handleChange)
  }

  collectElements() {
    this.answers = []
    this.currentIndex = 0
    this.formSucceeded = false
    this.isGuided =
      this.dataset.quizPresentation === QuizConfig.guidedPresentation
    this.answerInputs = Array.from(
      this.querySelectorAll(QuizSelector.answersInput)
    )
    this.resultsStep = this.querySelector(QuizSelector.resultsStep)
    this.status = this.querySelector(QuizSelector.status)
    this.storageKey = `${QuizConfig.storagePrefix}${this.id || 'quiz'}`
    this.nameStorageKey = `${QuizConfig.namePrefix}${this.id || 'quiz'}`
    this.visitorName = ''
  }

  initialStepIndex() {
    const initialStep = this.steps.findIndex(
      (step) => step.getAttribute('aria-hidden') === 'false'
    )
    return Math.max(initialStep, 0)
  }

  restoreName() {
    this.visitorName = readStoredName(this.nameStorageKey)
    const input = this.querySelector(QuizSelector.nameInput)
    if (input instanceof HTMLInputElement && this.visitorName && !input.value) {
      input.value = this.visitorName
    }
    this.syncName()
    this.personalizeTitles()
  }

  /** @returns {boolean} */
  captureName() {
    const input = this.querySelector(QuizSelector.nameInput)
    if (!(input instanceof HTMLInputElement)) {
      return true
    }

    const value = input.value.trim()
    if (!value) {
      input.focus()
      return false
    }

    this.visitorName = value
    writeStoredName(this.nameStorageKey, value)
    this.syncName()
    this.personalizeTitles()
    return true
  }

  syncName() {
    for (const field of this.querySelectorAll(QuizSelector.nameValue)) {
      if (field instanceof HTMLInputElement) {
        field.value = this.visitorName
      }
    }
  }

  personalizeTitles() {
    let isLeadingTitle = true
    for (const title of this.querySelectorAll(QuizSelector.personalize)) {
      if (!(title instanceof HTMLElement)) {
        continue
      }
      const base = title.dataset.quizBase || title.textContent || ''
      title.textContent = isLeadingTitle
        ? formatGreeting(this.visitorName, base)
        : base
      isLeadingTitle = false
    }
  }

  /** @param {string | undefined} action */
  runAction(action) {
    switch (action) {
      case QuizAction.start:
        if (this.captureName()) {
          this.goToStep(this.currentIndex + 1)
        }
        break
      case QuizAction.next:
        this.advanceQuestion()
        break
      case QuizAction.back:
        this.goToStep(this.currentIndex - 1)
        break
      case QuizAction.restart:
        this.restart()
        break
      default:
        break
    }
  }

  /** @param {HTMLElement} optionButton */
  answerOption(optionButton) {
    const currentStep = optionButton.closest(QuizSelector.step)
    const currentIndex = this.steps.indexOf(currentStep)
    if (!(currentStep instanceof HTMLElement) || currentIndex < 0) {
      return
    }

    this.answers[currentIndex] = readOptionAnswer(
      optionButton,
      currentStep,
      currentIndex
    )
    this.syncAnswers()
    this.goToFollowingStep()
  }

  advanceQuestion() {
    const step = this.steps[this.currentIndex]
    if (step?.dataset.quizStepType !== QuizStepType.question) {
      return
    }

    const choices = checkedChoices(step)
    if (!choices.length) {
      this.announce(this.status?.dataset.quizRequiredMessage || '')
      this.focusFirstChoice(step)
      return
    }

    this.answers[this.currentIndex] = readChoiceAnswer(step, choices)
    this.syncAnswers()
    this.goToFollowingStep()
  }

  goToFollowingStep() {
    let nextIndex = this.currentIndex + 1
    while (nextIndex < this.steps.length) {
      const step = this.steps[nextIndex]
      if (this.isStepAvailable(step)) {
        break
      }
      nextIndex += 1
    }
    this.goToStep(nextIndex)
  }

  /** @param {HTMLElement} [step] */
  isStepAvailable(step) {
    if (!step) {
      return false
    }
    if (step.dataset.quizStepType === QuizStepType.contact) {
      const form = step.querySelector('form')
      const isHidden =
        step.hidden ||
        step.style.display === 'none' ||
        (form && (form.hidden || form.style.display === 'none')) ||
        this.formSucceeded
      return !isHidden
    }
    return true
  }

  /**
   * @param {HTMLElement} step
   * @param {HTMLInputElement} choice
   */
  enforceSelectionLimit(step, choice) {
    const limit = Number.parseInt(step.dataset.quizSelectionLimit || '', 10)
    const selectedCount = checkedChoices(step).length

    if (!exceedsSelectionLimit(selectedCount, limit || 0)) {
      this.announce('')
      return
    }

    choice.checked = false
    this.announce(step.dataset.quizLimitMessage || '')
  }

  restart() {
    this.answers = []
    clearStoredAnswers(this.storageKey)
    for (const choice of checkedChoices(this)) {
      choice.checked = false
    }
    this.syncAnswerInputs()
    this.announce('')
    this.goToStep(0)
  }

  syncAnswers() {
    this.syncAnswerInputs()
    writeStoredAnswers(this.storageKey, this.answers)
  }

  syncAnswerInputs() {
    const formattedAnswers = formatAnswerNote(this.answers)
    for (const input of this.answerInputs) {
      input.value = formattedAnswers
    }
  }

  resumeAfterSubmit() {
    this.formSucceeded = Boolean(this.querySelector(QuizSelector.formSuccess))
    const formError = this.querySelector(QuizSelector.formError)
    if (!(this.formSucceeded || formError instanceof HTMLElement)) {
      return false
    }

    this.answers = readStoredAnswers(this.storageKey)
    restoreCheckedChoices(this.steps, this.answers)
    this.syncAnswerInputs()

    if (formError instanceof HTMLElement) {
      this.goToStep(this.contactStepIndex(), false)
      requestAnimationFrame(() => formError.focus())
      return true
    }

    this.showResults(false)
    return true
  }

  /** @param {boolean} shouldFocus */
  showResults(shouldFocus) {
    const selectedTags = collectAnswerTags(this.answers)
    const resultsIndex = this.steps.indexOf(this.resultsStep)

    if (resultsIndex < 0 || (!selectedTags.length && this.isGuided)) {
      this.goToStep(this.contactStepIndex(), shouldFocus)
      return
    }

    this.revealResults(selectedTags)
    this.goToStep(resultsIndex, shouldFocus)
  }

  contactStepIndex() {
    const contactIndex = this.steps.findIndex(
      (step) => step.dataset.quizStepType === QuizStepType.contact
    )
    if (contactIndex >= 0) {
      return contactIndex
    }
    return this.steps.length - 1
  }

  /** @param {string[]} selectedTags */
  revealResults(selectedTags) {
    if (!this.resultsStep) {
      return
    }
    const candidates = Array.from(
      this.resultsStep.querySelectorAll(QuizSelector.candidate)
    )
    const limit =
      Number.parseInt(this.resultsStep.dataset.quizResultsCount, 10) ||
      QuizConfig.defaultResultCount
    const ranked = rankCandidates(candidates, selectedTags, limit)
    const fallback =
      !this.isGuided && ranked.length === 0
        ? candidates
            .slice(0, limit)
            .map((element, position) => ({ element, position, score: 0 }))
        : ranked
    const emptyMessage = this.resultsStep.querySelector(
      QuizSelector.resultsEmpty
    )

    showRankedCandidates(candidates, fallback)
    if (emptyMessage instanceof HTMLElement) {
      emptyMessage.hidden = fallback.length > 0
    }
  }

  /**
   * @param {number} index
   * @param {boolean} [shouldFocus]
   */
  goToStep(index, shouldFocus = true) {
    const maxIndex = this.steps.length - 1
    this.currentIndex = Math.min(Math.max(index, 0), maxIndex)

    for (const [stepIndex, step] of this.steps.entries()) {
      this.toggleStep(step, stepIndex === this.currentIndex)
    }
    this.syncGuidedChrome()

    const currentStep = this.steps[this.currentIndex]
    if (
      currentStep === this.resultsStep ||
      currentStep?.dataset.quizStepType === QuizStepType.results
    ) {
      this.revealResults(collectAnswerTags(this.answers))
    }

    if (!shouldFocus) {
      return
    }
    this.focusStep(this.steps[this.currentIndex])
  }

  /**
   * @param {HTMLElement} step
   * @param {boolean} isCurrent
   */
  toggleStep(step, isCurrent) {
    step.setAttribute('aria-hidden', String(!isCurrent))
    step.toggleAttribute('inert', !isCurrent)
  }

  syncGuidedChrome() {
    if (!this.isGuided) {
      return
    }

    const stepType = this.steps[this.currentIndex].dataset.quizStepType || ''
    this.dataset.quizCurrent = stepType
    this.syncProgress()

    for (const element of this.querySelectorAll(QuizSelector.visibleOn)) {
      const visibleOn = (element.dataset.quizVisibleOn || '').split(' ')
      element.hidden = !visibleOn.includes(stepType)
    }
  }

  syncProgress() {
    const stepTypes = this.steps.map((step) => step.dataset.quizStepType || '')
    const progress = progressState(stepTypes, this.currentIndex)
    const progressBar = this.querySelector(QuizSelector.progressBar)
    const progressText = this.querySelector(QuizSelector.progressText)

    progressBar?.setAttribute('max', String(Math.max(progress.total, 1)))
    progressBar?.setAttribute('value', String(progress.current))

    if (progressText instanceof HTMLElement) {
      progressText.textContent = formatProgress(
        progressText.dataset.quizProgressTemplate || '',
        progress
      )
    }
  }

  /** @param {string} message */
  announce(message) {
    if (!(this.status instanceof HTMLElement)) {
      return
    }
    this.status.textContent = message
  }

  /** @param {HTMLElement} step */
  focusFirstChoice(step) {
    const firstChoice = step.querySelector(QuizSelector.choice)
    if (firstChoice instanceof HTMLElement) {
      firstChoice.focus()
    }
  }

  /** @param {HTMLElement} currentStep */
  focusStep(currentStep) {
    const selector = this.isGuided
      ? QuizSelector.focus
      : QuizSelector.legacyFocus
    const focusTarget = currentStep.querySelector(selector)

    if (!(focusTarget instanceof HTMLElement)) {
      return
    }

    requestAnimationFrame(() => {
      focusTarget.focus({ preventScroll: !this.isGuided })
    })
  }
}

if (!customElements.get('quiz-flow')) {
  customElements.define('quiz-flow', QuizFlow)
}
